/**
 * « Préparer l'année » d'un site et son contrat d'entretien (2026-10-30) — ce que l'écran en dit (rien n'y est coché au
 * chargement), ce qu'il envoie. Aucune règle de calcul ici : le serveur dit les visites dues, les dates, les paliers et les raisons.
 */
import type { BaseDuCalcul, ContratsDuSite, ContratVue, CreationSite, EntreeContrat, LigneEntretienSite, MachineContrat, MachinePreparee, PreparationSite } from '../api/client';
import { jourFr } from './planning';
import { heures } from './prochain-entretien';
import { phraseRetardEntretien } from './preparer-annee-entretiens';

export type ChoixVisiteDue = 'AVANCER' | 'SANS_PALIER';

export const BASES: readonly { readonly valeur: BaseDuCalcul; readonly libelle: string; readonly aide: string }[] = [
  { valeur: 'CONTRACT', libelle: 'Contrat', aide: 'Les visites suivent les heures vendues au contrat.' },
  { valeur: 'READINGS', libelle: 'Relevés', aide: 'Les visites suivent les heures relevées (les heures du contrat tant qu’il n’y a pas de relevés).' },
];

/** Le nom d'une machine, comme on la reconnaît. */
export function nomMachineSite(m: { readonly libelle: string | null; readonly modele: string | null }): string {
  return m.libelle ?? m.modele ?? 'Machine';
}

/** « 3 dues · 2 atteintes par les heures · 0 faite » ; rien pour une machine hors décompte (calendrier, sans heures au contrat). */
export function phraseCompteurs(m: MachinePreparee): string | null {
  if (m.dues === null) return null;
  const pl = (n: number, s: string, p: string) => `${n} ${n > 1 ? p : s}`;
  return `${pl(m.dues, 'visite due', 'visites dues')} · ${pl(m.atteintes ?? 0, 'atteinte', 'atteintes')} par les heures · ${pl(m.faites, 'faite', 'faites')}`;
}

/** D'où viennent les dates : « au rythme du contrat (8 760 h/an) », « d'après les relevés (6 000 h/an) »… */
export function phraseRegime(m: MachinePreparee): string | null {
  if (m.mode === 'CALENDAR') return 'au calendrier';
  if (m.regime === null) return null;
  const h = Math.round(m.regime.heuresParAn).toLocaleString('fr-FR');
  switch (m.regime.origine) {
    case 'CONTRAT':
      return `au rythme du contrat (${h} h/an)`;
    case 'MESURE':
      return `d’après les relevés (${h} h/an)`;
    default:
      return `au régime déclaré (${h} h/an)`;
  }
}

/** Quand : « en retard (point de 69 000 h) », « vers le 27/11/2026 · 72 000 h », « visite due, à placer dans l'année »… */
export function phraseQuandSite(l: LigneEntretienSite): string {
  const h = (n: number | null) => (n === null ? '' : heures(n));
  switch (l.genre) {
    case 'RETARD':
      return `En retard (point de ${h(l.cible)})`;
    case 'POINT':
      return l.date ? `Vers le ${jourFr(l.date)} · ${h(l.cible)}` : `${h(l.cible)} · date à estimer`;
    case 'ECHEANCE':
      return l.date ? `Le ${jourFr(l.date)}` : '—';
    case 'PREMIER':
      return 'Premier entretien à planifier';
    case 'DUE':
      return l.date ? `Visite due, vers le ${jourFr(l.date)}` : 'Visite due, à placer dans l’année du contrat';
    default:
      return '—';
  }
}

/** Le palier : « D », « D · révision », « À choisir » ; pour une visite due, celui qu'on avancerait. */
export function phrasePalierSite(l: LigneEntretienSite): string {
  const p = l.palier ?? null;
  if (p) return p.libelle ? `${p.code} · ${p.libelle}` : p.code;
  if (l.genre === 'DUE') return l.palierPropose ? `Aucun (le ${l.palierPropose.code} si on l’avance)` : 'Aucun';
  return 'À choisir';
}

/** Une intervention planifiée dont l'échéance a bougé : « L'échéance a bougé de 3 semaines (plus tard) ». */
export function phraseDecalage(jours: number | null): string | null {
  if (jours === null || jours === 0) return null;
  const n = Math.abs(jours);
  const duree = n >= 14 ? `${Math.round(n / 7)} semaines` : `${n} jours`;
  return `L’échéance a bougé de ${duree} (${jours > 0 ? 'plus tard' : 'plus tôt'}) : à décaler si besoin.`;
}

/** Le complément de « quand » d'une date passée : la même règle que la vue d'ensemble (date estimée d'un point au compteur). */
export function phraseRetardSite(l: LigneEntretienSite): string | null {
  return phraseRetardEntretien(l);
}

/** Le texte tapé pour les heures par an : entier de 1 à 8 760 ; vide = non précisé. */
export function lireHeuresParAn(brut: string): { valeur: number | null } | { erreur: string } {
  const t = brut.replace(/\s/g, '');
  if (t === '') return { valeur: null };
  if (!/^\d+$/.test(t)) return { erreur: 'Heures par an : un nombre entier.' };
  const n = Number(t);
  if (n < 1 || n > 8760) return { erreur: 'Heures par an : de 1 à 8 760.' };
  return { valeur: n };
}

export interface MachineSaisie {
  readonly id: string;
  readonly couverte: boolean;
  readonly heures: string;
  readonly base: BaseDuCalcul;
  /** Le nom de l'AUTRE contrat en cours qui couvre déjà la machine : elle ne se coche pas ici. */
  readonly prisePar: string | null;
}

export interface FormulaireContrat {
  readonly label: string;
  readonly signedOn: string;
  /** Vide = aucune visite de contrôle ; sinon un nombre de mois (1 = mensuelle). */
  readonly controle: string;
  readonly machines: readonly MachineSaisie[];
}

export const LIBELLE_CONTRAT_MAX = 80;

/**
 * Le formulaire d'un contrat (2026-11-03 : plusieurs par site) : prérempli par le contrat corrigé, ou vierge (nom à donner,
 * signature : aujourd'hui, aucune machine cochée). Les machines sont celles de TOUS les sites du client ; une machine déjà dans
 * un autre contrat en cours est montrée, mais ne se coche pas.
 */
export function formulaireDepuisContrat(vue: ContratsDuSite, contrat: ContratVue | null, aujourdhui: string): FormulaireContrat {
  return {
    label: contrat?.label ?? '',
    signedOn: contrat?.signedOn ?? aujourdhui,
    controle: contrat?.controlEveryMonths ? String(contrat.controlEveryMonths) : '',
    machines: vue.machinesDuClient.map((m) => {
      const ligne = contrat?.machines.find((l) => l.assetId === m.id) ?? null;
      return {
        id: m.id,
        couverte: ligne !== null,
        heures: ligne?.hoursPerYear == null ? '' : String(ligne.hoursPerYear),
        base: ligne?.basis ?? 'CONTRACT',
        prisePar: m.contrat !== null && m.contrat.id !== contrat?.id ? m.contrat.label : null,
      };
    }),
  };
}

/** Les machines du panneau : celles du site d'abord, puis (si on le demande, ou si le contrat en couvre) celles des autres sites. */
export function machinesDuPanneau(
  vue: ContratsDuSite,
  f: FormulaireContrat,
  autresSites: boolean,
): readonly { readonly info: MachineContrat; readonly saisie: MachineSaisie }[] {
  const infos = new Map(vue.machinesDuClient.map((m) => [m.id, m]));
  const toutes = f.machines.flatMap((saisie) => {
    const info = infos.get(saisie.id);
    return info ? [{ info, saisie }] : [];
  });
  const ici = toutes.filter((x) => x.info.siteId === vue.site.id);
  const ailleurs = toutes
    .filter((x) => x.info.siteId !== vue.site.id && (autresSites || x.saisie.couverte))
    .sort((a, b) => a.info.siteNom.localeCompare(b.info.siteNom));
  return [...ici, ...ailleurs];
}

/** Combien de machines des AUTRES sites du client le panneau peut proposer. */
export function machinesDesAutresSites(vue: ContratsDuSite): number {
  return vue.machinesDuClient.filter((m) => m.siteId !== vue.site.id).length;
}

/** Ce que l'enregistrement envoie, ou la première erreur à dire. */
export function corpsContrat(f: FormulaireContrat, revision: number | null): { corps: EntreeContrat } | { erreur: string } {
  const label = f.label.replace(/\s+/g, ' ').trim();
  if (label === '') return { erreur: 'Donnez un nom au contrat (ex. « Compresseurs »).' };
  if (label.length > LIBELLE_CONTRAT_MAX) return { erreur: `Nom du contrat : ${LIBELLE_CONTRAT_MAX} caractères au plus.` };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.signedOn)) return { erreur: 'Date de signature : choisissez une date.' };
  let controle: number | null = null;
  if (f.controle.trim() !== '') {
    const n = Number(f.controle);
    if (!Number.isInteger(n) || n < 1 || n > 12) return { erreur: 'Visites de contrôle : tous les 1 à 12 mois, ou aucune.' };
    controle = n;
  }
  const machines: { assetId: string; hoursPerYear: number | null; basis: BaseDuCalcul }[] = [];
  for (const m of f.machines) {
    if (!m.couverte || m.prisePar !== null) continue;
    const h = lireHeuresParAn(m.heures);
    if ('erreur' in h) return { erreur: h.erreur };
    machines.push({ assetId: m.id, hoursPerYear: h.valeur, basis: m.base });
  }
  return { corps: { revision, label, signedOn: f.signedOn, controlEveryMonths: controle, machines } };
}

/** La date de fin d'un contrat : une vraie date, pas avant la signature, pas dans le futur. */
export function lireDateFin(brut: string, signedOn: string, aujourdhui: string): { date: string } | { erreur: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(brut)) return { erreur: 'Date de fin : choisissez une date.' };
  if (brut < signedOn) return { erreur: `La date de fin ne peut pas précéder la signature (${jourFr(signedOn)}).` };
  if (brut > aujourdhui) return { erreur: 'La date de fin ne peut pas être dans le futur : terminez le contrat le jour venu.' };
  return { date: brut };
}

/** « signé le 15/03/2025 · une visite de contrôle tous les 3 mois ». */
export function phraseContrat(c: { readonly signedOn: string; readonly controlEveryMonths: number | null }): string {
  const controle = c.controlEveryMonths
    ? c.controlEveryMonths === 1
      ? 'une visite de contrôle par mois'
      : `une visite de contrôle tous les ${c.controlEveryMonths} mois`
    : 'pas de visite de contrôle';
  return `signé le ${jourFr(c.signedOn)} · ${controle}`;
}

/** « 6 machines » / « 1 machine » / « aucune machine ». */
export function phraseMachines(n: number): string {
  return n === 0 ? 'aucune machine' : `${n} machine${n > 1 ? 's' : ''}`;
}

/** Ce que la création envoie : des identifiants et, pour une visite due, son choix. */
export function corpsCreationSite(entree: {
  readonly vue: PreparationSite;
  readonly natureId: string;
  readonly natureControleId: string;
  readonly entretiens: ReadonlySet<string>;
  readonly choix: ReadonlyMap<string, ChoixVisiteDue>;
  readonly controles: ReadonlySet<string>;
  readonly echeances: ReadonlySet<string>;
}): CreationSite {
  return {
    decalage: entree.vue.annee.decalage,
    natureId: entree.natureId,
    natureControleId: entree.controles.size > 0 ? entree.natureControleId : null,
    entretiens: [...entree.entretiens].map((id) => ({ id, choix: entree.choix.get(id) ?? null })),
    controles: [...entree.controles],
    obligationIds: [...entree.echeances],
  };
}

/** Peut-on créer ? Sinon, ce qui manque. */
export function peutCreerSite(entree: {
  readonly vue: PreparationSite;
  readonly natureId: string;
  readonly natureControleId: string;
  readonly entretiens: ReadonlySet<string>;
  readonly choix: ReadonlyMap<string, ChoixVisiteDue>;
  readonly controles: ReadonlySet<string>;
  readonly echeances: ReadonlySet<string>;
}): { ok: true } | { ok: false; erreur: string } {
  const total = entree.entretiens.size + entree.controles.size + entree.echeances.size;
  if (total === 0) return { ok: false, erreur: 'Cochez au moins une ligne.' };
  if ((entree.entretiens.size > 0 || entree.echeances.size > 0) && entree.natureId === '') {
    return { ok: false, erreur: 'Choisissez la nature des interventions d’entretien.' };
  }
  if (entree.controles.size > 0 && entree.natureControleId === '') {
    return { ok: false, erreur: 'Choisissez la nature des visites de contrôle.' };
  }
  const dues = new Set(entree.vue.machines.flatMap((m) => m.lignes.filter((l) => l.genre === 'DUE').map((l) => l.id)));
  for (const id of entree.entretiens) {
    if (dues.has(id) && !entree.choix.has(id)) return { ok: false, erreur: 'Une visite due cochée attend votre choix : avancer le prochain palier, ou visite sans palier.' };
  }
  return { ok: true };
}

/** L'année du contrat, en clair : « du 20/10/2026 au 19/10/2027 ». */
export function phraseAnnee(a: { readonly debut: string; readonly fin: string }): string {
  return `du ${jourFr(a.debut)} au ${jourFr(a.fin)}`;
}
